/**
 * Region-aware feature paywall content system.
 *
 * The feature paywall is shown when a user tries to access a premium feature
 * that requires an upgrade. Content varies by region:
 *  - US regions (TX, CA, OTHER): Emphasizes school plan requirement
 *  - International (INTL): Emphasizes upgrade path
 *
 * All user-facing strings are wrapped in Tolgee t() for localization.
 */

import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';

import { PaywallRegion, resolveRegion } from '../LessonLimitPaywall/paywallContent';

export { resolveRegion };

export interface FeaturePaywallContent {
  title: string;
  subtitle: string;
  features: string[];
  ctaText: string;
  ctaLink: string;
}

export function useFeaturePaywallContent(region: PaywallRegion) {
  const { t } = useTranslate();

  const content = useMemo((): FeaturePaywallContent => {
    const isUS = region !== 'INTL';

    if (isUS) {
      return {
        title: t('FeaturePaywall.Title.US', 'This feature requires the school plan'),
        subtitle: t(
          'FeaturePaywall.Subtitle.US',
          'To unlock this feature, ask your school administrator about upgrading to the Curipod school plan.',
        ),
        features: [
          t('FeaturePaywall.Features.US.1', 'Unlimited teaching sessions'),
          t('FeaturePaywall.Features.US.2', 'Full AI feedback capabilities'),
          t('FeaturePaywall.Features.US.3', 'Extended character limits'),
        ],
        ctaText: t('FeaturePaywall.CTA.US', 'I need access now — get help'),
        ctaLink: '/school-plan-help',
      };
    }

    return {
      title: t('FeaturePaywall.Title.INTL', 'Upgrade to use this feature'),
      subtitle: t(
        'FeaturePaywall.Subtitle.INTL',
        'Get full access to Curipod for you or your whole school. Visit our pricing page to explore plans and get started today.',
      ),
      features: [
        t('FeaturePaywall.Features.INTL.1', 'Unlimited teaching sessions'),
        t('FeaturePaywall.Features.INTL.2', 'Full AI feedback capabilities'),
        t('FeaturePaywall.Features.INTL.3', 'Extended character limits'),
      ],
      ctaText: t('FeaturePaywall.CTA.INTL', 'See plans and pricing'),
      ctaLink: '/pricing',
    };
  }, [region, t]);

  return content;
}
